import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AIServices = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const services = [
    {
      id: 1,
      title: "AI-Powered Analytics",
      description: "Leverage machine learning to gain actionable insights from your data and make informed business decisions.",
      icon: "📊",
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: 2,
      title: "Predictive Modeling",
      description: "Forecast trends, customer behavior, and market changes with our advanced predictive algorithms.",
      icon: "🔮",
      color: "from-purple-500 to-pink-500"
    },
    {
      id: 3,
      title: "Natural Language Processing",
      description: "Transform how you interact with data through our advanced NLP and text analysis solutions.",
      icon: "💬",
      color: "from-green-500 to-teal-500"
    },
    {
      id: 4,
      title: "Computer Vision",
      description: "Implement image recognition and processing capabilities to automate visual tasks.",
      icon: "👁️",
      color: "from-red-500 to-orange-500"
    },
    {
      id: 5,
      title: "Intelligent Automation",
      description: "Streamline operations with AI-driven process automation that learns and improves over time.",
      icon: "⚙️",
      color: "from-indigo-500 to-blue-500"
    },
    {
      id: 6,
      title: "AI Consulting",
      description: "Get expert guidance on implementing AI solutions tailored to your specific business needs.",
      icon: "🧠",
      color: "from-yellow-500 to-amber-500"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 0.77, 0.47, 0.97]
      }
    }
  };

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-20">
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-purple-600 rounded-full mix-blend-soft-light filter blur-xl animate-pulse" />
        <div className="absolute top-1/2 right-10 w-72 h-72 bg-cyan-500 rounded-full mix-blend-soft-light filter blur-xl animate-pulse animation-delay-2000" />
        <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-blue-600 rounded-full mix-blend-soft-light filter blur-xl animate-pulse animation-delay-4000" />
      </div>

      <div className=" mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">AI Services</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            Leverage the power of artificial intelligence to transform your business processes,
            gain valuable insights, and stay ahead of the competition.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-xl border border-gray-700 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              variants={itemVariants}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 bg-gradient-to-r ${service.color} text-white text-2xl`}>
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
              <p className="text-gray-300">{service.description}</p>

            </motion.div>
          ))}
        </motion.div>


      </div>

      {/* Floating elements */}
      <div className="absolute bottom-10 left-10 w-4 h-4 rounded-full bg-cyan-400 opacity-50 animate-bounce" />
      <div className="absolute top-1/4 right-20 w-6 h-6 rounded-full bg-purple-500 opacity-30 animate-bounce animation-delay-2000" />
      <div className="absolute top-2/3 left-1/4 w-3 h-3 rounded-full bg-blue-400 opacity-40 animate-bounce animation-delay-4000" />
    </section>
  );
};

export default AIServices;